import type { SourceCandidate } from '../../shared/contract/library.js';
import { formatCurrencyText } from './currency-format.js';
import { formatDateOnly } from './format.js';

/** A source row's paid price and date, e.g. `PKR 16,800 on Feb 18, 2026`, or null without a price. */
export function sourceRowPaid(row: Pick<SourceCandidate, 'unitPrice' | 'currency' | 'purchasedOn'>): string | null {
  if (row.unitPrice === null || row.currency === null) return null;
  const amount = Number(row.unitPrice);
  // Keep the row's own decimals (at least two once it has any): 125.50 stays "125.50", 16800 stays whole.
  const decimals = row.unitPrice.split('.')[1]?.length ?? 0;
  const digits = decimals === 0 ? 0 : Math.min(Math.max(decimals, 2), 4);
  const price = Number.isFinite(amount)
    ? formatCurrencyText(amount, row.currency, digits)
    : `${row.currency} ${row.unitPrice}`;
  return row.purchasedOn === null ? price : `${price} on ${formatDateOnly(row.purchasedOn)}`;
}

/**
 * Where an inventory or purchase row came from and what it says, on one line under its
 * description: kind, document and row, quantity, SKU, paid price and date, and supplier. Shared
 * by the Find a Source Row dialog and the demand's Inventory and Purchase History section
 * (HS2-4TER5Q, HS2-YENW3T).
 */
export function sourceRowProvenance(row: SourceCandidate): string {
  const where = `${row.documentName} · ${row.sheet === null ? '' : `${row.sheet} `}row ${String(row.sourceRow)}`;
  const amount = [row.quantity, row.unit].filter((part) => part !== null).join(' ');
  return [
    row.kind === 'inventory' ? 'Stock' : 'Purchase',
    where,
    amount === '' ? null : `Qty ${amount}`,
    row.sku === null ? null : `SKU ${row.sku}`,
    sourceRowPaid(row),
    row.vendor,
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
}

/** A source row's match status as a title-style label. */
export function sourceRowStatusLabel(status: SourceCandidate['status']): string {
  return status === 'confirmed' ? 'Matched' : status === 'rejected' ? 'Rejected' : 'Suggested';
}
